/**
 * Created by tom on 14/03/17.
 */
import {ErrorMessages} from "../utility/error-messages";
import {WKT} from "ol/format";
import {Geometry, MultiPolygon, Polygon} from "ol/geom";
import {IPortalServices} from "../lt-ng/portal-services";

export interface ILayerQueryRequest {
	geometry: Geometry;
	epsg: string;
	scienceLayers: string[];
	layerIds: number[];
}
/*
// unused interfaces
export interface ILayerQueryResultSoils {
	area:			number;
	classNo:		number;
	description:	string;
}

export interface ILayerQueryResultSiblings {
	area:		number;
	name:		string;
	proportion:	number;
}
*/
/*
export interface ISiblingQueryResult {
	soils : 		ILayerQueryResultSoils[];
	unclassified:	number;
	paw:			number;
	totalSibling:	number;
	totalArea:		number;
	totalSmapArea:	number;
	siblings:		ILayerQueryResultSiblings[]
}
*/

export interface IUnitOfMeasurement {
	abbrName: string;
	fullName: string;
}

export interface IColumn {
	name: string;
	value: string|number| { [key: string]: any };
	colour?: string;
	unitOfMeasurement?: IUnitOfMeasurement;
}

export interface IRow {
	columns: IColumn[]
	number: number
}

export interface ILayerQueryLayer {
	name: string;
	raster: boolean;
	rows: IRow[];
}

export interface ILayerQueryResult {
	response: {
        smapUnclassified?: number;
		layers: ILayerQueryLayer[];
		pointHeight: number;
		area?: number;
	}
}

export interface ISMapQuerySoil {
	area: number;
	classNo: number;
	description: string;
}

export interface ISMapQuerySibling {
	area: number;
	name: string;
	proportion: number;
}

export interface ISMapQueryResult {
	response:	{
		soils:	ISMapQuerySoil[];
		unclassified: number;
		paw: number;
		totalSiblings: number;
		totalArea: number;
		totalSmapArea: number;
		siblings: ISMapQuerySibling[]
	}
}

export interface ILayerQueryService {
	query: (request: ILayerQueryRequest) => IRequestAndCancel<ILayerQueryResult>;
	querySMap: (request: ILayerQueryRequest) => IRequestAndCancel<ISMapQueryResult>;
	flattenData : (data : ILayerQueryResult) => any;
}

export interface IRequestAndCancel<T> {
	request : ng.IPromise<T>;
	cancel?: ng.IDeferred<any>;
	retry?: () => void;
}
export class LayerQueryService implements ILayerQueryService {
	public static $inject = ['$http', '$q', 'portalServices'];
	constructor(protected $http : ng.IHttpService, protected $q : ng.IQService, protected portalServices: IPortalServices) {

	}
	protected url = `${this.portalServices.baseUri()}/layer_query/area_byIds/json`;

	public querySMap(request : ILayerQueryRequest) : IRequestAndCancel<ISMapQueryResult> {
		let url = `${this.portalServices.baseUri()}/layer_query/area/smap/json`;
		return this.areaQuery(url, request);
	}

	/**
	 * $http needs to be configured to get this request right. The
	 * polygon needs to be sent as form data, while the other data
	 * needs to be sent via url parameters.
	 * @param request
	 * @returns ng.IPromise<ILayerQueryResult>
	 */
	public query(request : ILayerQueryRequest) : IRequestAndCancel<ILayerQueryResult> {
		let url = `${this.portalServices.baseUri()}/layer_query/area_byIds/json`;
		return this.areaQuery(url, request);
	}

	public areaQuery<T>(url: string, request : ILayerQueryRequest) : IRequestAndCancel<T> {
		let format = new WKT();

		const geom = request.geometry;
		let flattendGeom: Geometry = geom;

		// the service expects 2d coordinates with the format XY
		if (geom instanceof Polygon
			&& geom.getLayout() !== 'XY') {
			flattendGeom = new Polygon(
				geom.getCoordinates().map((ring) => ring.map(([x, y]) => [x, y]))
			);
		} else if (geom instanceof MultiPolygon
			&& geom.getLayout() !== 'XY') {
			flattendGeom = new MultiPolygon(
				geom.getCoordinates().map(
					poly => poly.map(ring => ring.map(([x, y]) => [x, y]))
				)
			);
		}

		// the payload needs to be www-form-urlencoded, not json
		let data = "polygon=" + encodeURIComponent(format.writeGeometry(flattendGeom));
		let cancel = this.$q.defer();

		if (data.length > 1000 * 1000 * 1.5) {
			return {
				request: this.$q.reject(ErrorMessages.reportRequestTooComplex),
				cancel
			}
		}

		let makeRequest = (aCancel: ng.IDeferred<any>) => {
			return this.$http<T>({
				url: url,
				// payload
				data : data,
				timeout: aCancel.promise,
				// url params
				params : {
					layers : request.scienceLayers.join(","),
					layerIds: request.layerIds,
					epsg: request.epsg
				},
				method: 'POST',
				headers: {'Content-Type': 'application/x-www-form-urlencoded'}
			}).then((resp) => resp.data,
				(reason) => {
					console.log(reason);
					if (reason.status === 504) {
						return this.$q.reject(ErrorMessages.areaRequestTimedOut);
					}
					return this.$q.reject(ErrorMessages.reportRequestFailed)
				});
		}

		let result: IRequestAndCancel<T> = {
			request : makeRequest(cancel),
			cancel
		};

		result.retry = () => {
			result.cancel.resolve(true);
			let newCancel = this.$q.defer();
			result.request = makeRequest(newCancel);
			result.cancel = newCancel;
		}

		return result;
	}

	protected toObject(arr : [[string, any]]) : Object {
		let d = {};
		arr.forEach((pair) => d[pair[0]] = pair[1]);
		return d;
	}

	protected rowToObject(row) : Object {
		return this.toObject(row.columns.map((c) => [c.name, c.value]));
	}
	/**
	 * @param data
	 * @returns [string, string, number][]
	 */
	public flattenData(data : ILayerQueryResult) : any {
		let layers = data && data.response && data.response.layers || [];
		return layers.map((layer) => {
			return layer.rows.map((row) => this.rowToObject(row))
				.filter((row) => (row['Class Flags'] & 1) !== 0)
				.map((row) => {
					let area = row['Class Area'];
					return [
						row['Class'] || '',
						row['Class Description'] || '',
						area === '' || area === undefined ? '' : Number(area || 0)
					];
				});
		});
	}
}
